import { useState, useRef, useEffect, useCallback } from "react";
import "../../assets/scss/style.css";
import { Helmet } from "react-helmet-async";

/*
  BetcaTwinFinder — React port of the BeTCA Tech Meet 2026 "Find Your Twin" quiz.

  Notes on the port:
  - The whole design is preserved. Styles live in the <style> block at the
    bottom of the component (global class names, same as the original HTML).
  - Analytics: the original loaded gtag.js in <head>. This component does NOT
    inject that script — add your GA4 snippet to your app's index.html once,
    and track() below will use window.gtag if it's present (and always logs
    to the console).
  - To add a company: push another object into `companies` with the same
    shape. tag1/tag2 must be keys of TAG_LABELS. Add an optional `photo`
    (URL or path) to show a real founder photo instead of the emoji avatar.
*/

/* ================= DATA ================= */
const QUIZ_URL = "https://mili8247.github.io/betca-twinmatch-quiz/";

const TAG_LABELS = {
  AI: "Artificial Intelligence",
  HARDWARE: "Hardware & Robotics",
  SECURITY: "Cybersecurity",
  AUTO: "Automotive & Aerospace",
  FINTECH: "FinTech & Accounting",
  HEALTH: "HealthTech",
  MARKETING: "Marketing & Branding",
  CLOUD: "Cloud & Software",
  EDU: "EdTech",
  LOCAL: "Local & Community",
  INDUSTRIAL: "Industrial & Manufacturing",
};

const companies = [
  {
    name: "Samhita Engineering LLP",
    founder: "Sandeep Patil · Founder",
    emoji: "🛩️",
    tag1: "AUTO",
    tag2: "HARDWARE",
    sector: "Aerospace & Automotive Software",
    blurb:
      "You'll click over safety-critical engineering software for planes, cars, trains, medical devices, and even spacecraft — the kind that can't afford to be wrong.",
  },
  {
    name: "M V Solutions",
    founder: "Ram Kattige · ERP Consultant",
    emoji: "🧾",
    tag1: "FINTECH",
    tag2: "CLOUD",
    sector: "ERP & Compliance",
    blurb:
      "You'll click over ERP rollouts (TallyPrime & Odoo) and hands-on training that make GST, payroll, and compliance make sense in Udyambag and beyond.",
  },
  {
    name: "Dattamsh",
    founder: "Chetana Sarang · Founder, CEO",
    emoji: "🎓",
    tag1: "EDU",
    tag2: "AI",
    sector: "AI Education",
    blurb:
      "You'll click over teaching AI to literal classrooms — from Class 1 to boardrooms.",
  },
  {
    name: "PROEN",
    founder: "Mukund Kagatikar · Business Development",
    emoji: "📊",
    tag1: "AI",
    tag2: "CLOUD",
    sector: "AI & Data Analytics",
    blurb:
      "You'll click over enterprise AI and data — the unglamorous engine behind the glamorous demos.",
  },
  {
    name: "Sophon Technologies",
    founder: "Veeresh Ambe · CEO",
    emoji: "🚗",
    tag1: "AUTO",
    tag2: "HARDWARE",
    sector: "Automotive Software",
    blurb:
      "You'll click over building India's own homegrown automotive software stack, from scratch.",
  },
  {
    name: "Ecobillz India",
    founder: "Santosh Hiremath · Director",
    emoji: "🏨",
    tag1: "LOCAL",
    tag2: "AI",
    sector: "Hospitality Tech",
    blurb:
      "You'll click over an AI-driven digitization platform quietly running the back office for hotels across India, the UAE, and the UK.",
  },
  {
    name: "WeTrunk IT Solutions",
    founder: "Sachin Kulgod · Founder, CEO",
    emoji: "🧵",
    tag1: "FINTECH",
    tag2: "CLOUD",
    sector: "Niche SaaS",
    blurb:
      "You'll click over niche SaaS — ambulance reporting, textile billing, credit scoring for co-ops.",
  },
  {
    name: "Theta Dynamics",
    founder: "Gajendra Deshpande · Managing Director",
    emoji: "🔐",
    tag1: "SECURITY",
    tag2: "CLOUD",
    sector: "Cybersecurity",
    blurb:
      "You'll click over penetration testing and closing the gap before someone else finds it.",
  },
  {
    name: "Ajinkya Technologies",
    founder: "Amey Kadle · CEO",
    emoji: "🏭",
    tag1: "HARDWARE",
    tag2: "INDUSTRIAL",
    sector: "IoT & Factory Automation",
    blurb:
      "You'll click over factory floors that run themselves, tracked down to the last dispatch.",
  },
  {
    name: "Innovv8",
    founder: "Sandeep Tekriwal · CEO",
    emoji: "🖥️",
    tag1: "HARDWARE",
    tag2: "CLOUD",
    sector: "IT Hardware",
    blurb:
      "You'll click over the actual hardware — servers, laptops, and enough GPU power to run anything.",
  },
  {
    name: "Akxa Tech Pvt. Ltd.",
    founder: "Raghuraj Rao · MD, Aqua Alloys",
    emoji: "⚙️",
    tag1: "INDUSTRIAL",
    tag2: "HARDWARE",
    sector: "Industrial Process Tech",
    blurb:
      "You'll click over squeezing ROI out of process plants in under six months, patents included.",
  },
  {
    name: "Sarus Aerospace",
    founder: "Rahul Patil · Founder, CEO",
    emoji: "🛸",
    tag1: "AUTO",
    tag2: "HARDWARE",
    sector: "Drones & UAVs",
    blurb:
      "You'll click over drones — real ones, built here, flying defense and agri missions.",
  },
  {
    name: "Evobi Automations",
    founder: "Sandeep Patil · CTO",
    emoji: "🤖",
    tag1: "HARDWARE",
    tag2: "EDU",
    sector: "Robotics & Hardware Design",
    blurb:
      "You'll click over award-winning robots that teach kids to code by dragging and dropping.",
  },
  {
    name: "Reputes Business Solutions",
    founder: "Supreet Dixit · CEO",
    emoji: "📣",
    tag1: "MARKETING",
    tag2: "AI",
    sector: "AI Marketing",
    blurb:
      "You'll click over AI that actually moves the marketing ROI needle, 10x and up.",
  },
  {
    name: "AnexGATE",
    founder: "Hitesh Dharmdasani · CEO",
    emoji: "🛡️",
    tag1: "SECURITY",
    tag2: "CLOUD",
    sector: "Network Security",
    blurb:
      "You'll click over 'Make in India' network security — firewalls, VPNs, the whole gateway.",
  },
  {
    name: "Vayavya Labs Pvt. Ltd.",
    founder: "RK Patil · CEO",
    emoji: "🧠",
    tag1: "AUTO",
    tag2: "HARDWARE",
    sector: "Embedded Systems",
    blurb:
      "You'll click over the deep-tech bridge between hardware and software — 11 patents deep.",
  },
  {
    name: "Deltasys eForming",
    founder: "Virendra Kadam & Murlidhar Sutar · Founders",
    emoji: "🖨️",
    tag1: "INDUSTRIAL",
    tag2: "HARDWARE",
    sector: "3D Printing & Manufacturing",
    blurb:
      "You'll click over 3D printing entire homes, one concrete layer at a time.",
  },
  {
    name: "Markecha",
    founder: "Rushikesh Bhurse · Founder",
    emoji: "📈",
    tag1: "MARKETING",
    tag2: "CLOUD",
    sector: "Digital Marketing",
    blurb:
      "You'll click over full-stack digital marketing that's actually ROI-obsessed.",
  },
  {
    name: "DesiLLM Pvt. Ltd.",
    founder: "Kawal Arora · CEO",
    emoji: "✨",
    tag1: "AI",
    tag2: "CLOUD",
    sector: "GenAI Platforms",
    blurb:
      "You'll click over building GenAI apps fast — from AI books to AI sales bots.",
  },
  {
    name: "SenseGiz",
    founder: "Abhishek Latthe · CEO",
    emoji: "🛰️",
    tag1: "HARDWARE",
    tag2: "INDUSTRIAL",
    sector: "Enterprise & Defence Tech",
    blurb:
      "You'll click over multidisciplinary tech built for enterprise and defence-grade problems.",
  },
  {
    name: "Outofbox Cloud Pvt. Ltd.",
    founder: "Ajit Patil · Founder, CEO",
    emoji: "☁️",
    tag1: "CLOUD",
    tag2: "SECURITY",
    sector: "Cloud Infrastructure",
    blurb:
      "You'll click over cloud infrastructure that's fast, local, and doesn't cost the earth.",
  },
  {
    name: "Alphasure Accountants",
    founder: "Onkar Marathe · Founder",
    emoji: "📚",
    tag1: "FINTECH",
    tag2: "LOCAL",
    sector: "Accounting",
    blurb: "You'll click over books that are actually, properly, in order.",
  },
  {
    name: "DoorTaq Technologies",
    founder: "Rajiv Raykar · Director & Chairman",
    emoji: "🛵",
    tag1: "LOCAL",
    tag2: "MARKETING",
    sector: "Local Delivery & Commerce",
    blurb:
      "You'll click over getting anything — groceries to mangoes — delivered across Belagavi in minutes.",
  },
  {
    name: "Vedita AI",
    founder: "Dr. Savita Mathur",
    emoji: "🕉️",
    tag1: "HEALTH",
    tag2: "AI",
    sector: "HealthTech / AI Education",
    blurb:
      "You'll click over AI that's teaching Ayurveda to a new generation of practitioners.",
  },
  {
    name: "Malenadu Natural Producer Co.",
    founder: "Sandeep Hegde · Director",
    emoji: "🌿",
    tag1: "LOCAL",
    tag2: "HEALTH",
    sector: "AgriTech & Natural Produce",
    blurb:
      "You'll click over produce that goes straight from farm to your door, lab-tested.",
  },
  {
    name: "BTP Infoserve",
    founder: "Tushar Patil · Director",
    emoji: "🏥",
    tag1: "HEALTH",
    tag2: "FINTECH",
    sector: "Healthcare BPO",
    blurb:
      "You'll click over healthcare billing for the US market, run out of Belagavi since 2001.",
  },
  {
    name: "AJR Curevibes Pvt. Ltd.",
    founder: "Akash Gadgade · Director",
    emoji: "💊",
    tag1: "HEALTH",
    tag2: "EDU",
    sector: "Healthcare Education",
    blurb:
      "You'll click over affordable clinical education and mentorship for healthcare founders.",
  },
  {
    name: "Vigilomics Biosciences",
    founder: "Dr. Shashikant Kulgod · Director",
    emoji: "🐕",
    tag1: "HEALTH",
    tag2: "AI",
    sector: "BioTech / Cancer Detection",
    blurb:
      "You'll click over dogs trained to sniff out cancer, backed by actual AI and ML.",
  },
  {
    name: "Fortune InfoServe",
    founder: "Praveen Bangodi · Managing Director",
    emoji: "🏦",
    tag1: "FINTECH",
    tag2: "CLOUD",
    sector: "Core Banking Software",
    blurb:
      "You'll click over core banking software that keeps small credit cooperatives running.",
  },
  {
    name: "Ohms Power",
    founder: "Baburao Babchede · Proprietor",
    emoji: "🔋",
    tag1: "HARDWARE",
    tag2: "INDUSTRIAL",
    sector: "Power & Backup Solutions",
    blurb:
      "You'll click over the UPS and backup power that keeps everything else on this list running.",
  },
  {
    name: "Zenigma Labs Pvt. Ltd.",
    founder: "Siddaling Basavaraj Latti · Co-Founder",
    emoji: "🧩",
    tag1: "CLOUD",
    tag2: "INDUSTRIAL",
    sector: "Industry 4.0 Consulting",
    blurb:
      "You'll click over ZeNii — a low-code platform stitching manufacturing, CRM, HR, and finance into one system.",
  },
  {
    name: "Shrigeet Solutions",
    founder: "Amit Netalkar · Partner",
    emoji: "🏫",
    tag1: "CLOUD",
    tag2: "EDU",
    sector: "ERP & Institutional Software",
    blurb:
      "You'll click over ERP, CAD and management software built for hospitals and colleges.",
  },
  {
    name: "All About Belgaum",
    founder: "Uday Kinjawadekar · Founder",
    emoji: "📰",
    tag1: "LOCAL",
    tag2: "MARKETING",
    sector: "Local Media & Storytelling",
    blurb:
      "You'll click over telling Belagavi's own stories, before anyone else does.",
  },
  {
    name: "Ahava Solutions",
    founder: "Rushiraj Deshpande · Partner",
    emoji: "💼",
    tag1: "FINTECH",
    tag2: "CLOUD",
    sector: "Finance & Accounting BPO",
    blurb:
      "You'll click over the finance back-office work that keeps other companies honest.",
  },
  {
    name: "Playtech",
    founder: "Vibha Kashyap · Co-Founder",
    emoji: "🔒",
    tag1: "SECURITY",
    tag2: "CLOUD",
    sector: "Privacy-First Apps",
    blurb:
      "You'll click over privacy-first apps built exactly to spec, nothing off-the-shelf.",
  },
  {
    name: "Shreshta IT Technologies",
    founder: "Swapneel Patnekar · Managing Director",
    emoji: "🕵️",
    tag1: "SECURITY",
    tag2: "CLOUD",
    sector: "Cybersecurity & Digital Forensics",
    blurb:
      "You'll click over hunting cyber threats and building the mobile forensics tools law enforcement actually uses.",
  },
  {
    name: "Simply Smart Labs",
    founder: "Shreyans Bhandari · CEO",
    emoji: "💡",
    tag1: "HARDWARE",
    tag2: "INDUSTRIAL",
    sector: "Product Design & 3D Printing",
    blurb:
      "You'll click over 3D-printing prototypes and designing lighting products that turn ideas into things you can hold.",
  },
  {
    name: "IP-FY",
    founder: "Harsh Pangi · Founder",
    emoji: "⚖️",
    tag1: "LOCAL",
    tag2: "EDU",
    sector: "IP Law & Startup Mentorship",
    blurb:
      "You'll click over patents, trademarks, and IP strategy for founders — plus mentoring startups from Stanford Seed to IIT Ropar.",
  },
  {
    name: "Bumshum",
    founder: "Mili Mishra · CEO",
    emoji: "🧸",
    tag1: "LOCAL",
    tag2: "CLOUD",
    sector: "Sustainable Retail & RetailTech",
    blurb:
      "You'll click over building a proprietary retail system for an eco-friendly kids' store — 4 stores, 2 cities, 30,000+ customers and counting.",
  },
];

const questions = [
  {
    q: "What almost never fails to pull your attention in a tech headline?",
    opts: [
      { t: "AI that can actually do something useful", tag: "AI" },
      { t: "A gadget or robot you could hold in your hand", tag: "HARDWARE" },
      { t: "A hack, breach, or 'your data isn't safe' story", tag: "SECURITY" },
      { t: "Something about self-driving cars or drones", tag: "AUTO" },
    ],
  },
  {
    q: "Pick a problem you'd actually want to help fix:",
    opts: [
      {
        t: "Small businesses drowning in paperwork and compliance",
        tag: "FINTECH",
      },
      { t: "Patients not getting the right care fast enough", tag: "HEALTH" },
      { t: "Great products nobody's ever heard of", tag: "MARKETING" },
      {
        t: "Local businesses invisible outside their own street",
        tag: "LOCAL",
      },
    ],
  },
  {
    q: "Your ideal Sunday-afternoon rabbit hole is...",
    opts: [
      { t: "Reading about a new AI model", tag: "AI" },
      {
        t: "Watching factory or manufacturing process videos",
        tag: "INDUSTRIAL",
      },
      { t: "Learning literally anything new online", tag: "EDU" },
      { t: "Comparing cloud storage plans, for fun", tag: "CLOUD" },
    ],
  },
  {
    q: "A friend asks you to explain what 'the cloud' actually is. You...",
    opts: [
      { t: "Explain it with one clean analogy, no jargon", tag: "CLOUD" },
      { t: "Redirect to what really matters: is it secure?", tag: "SECURITY" },
      { t: "Mention machines learn this stuff too, now", tag: "AI" },
      { t: "Admit you'd rather just show them", tag: "HARDWARE" },
    ],
  },
  {
    q: "₹10,000 to invest in a local startup tomorrow. Which pitch wins you over?",
    opts: [
      { t: "A device that fixes something physical, well", tag: "HARDWARE" },
      { t: "A drone or aerospace idea nobody's tried locally", tag: "AUTO" },
      { t: "A platform that makes a whole industry safer", tag: "SECURITY" },
      {
        t: "One that keeps money moving smarter for small firms",
        tag: "FINTECH",
      },
    ],
  },
  {
    q: "Which 'behind the scenes' job would you secretly love?",
    opts: [
      { t: "Teaching a room full of people something new", tag: "EDU" },
      { t: "Telling a city's stories nobody else is telling", tag: "LOCAL" },
      { t: "Making a brand impossible to ignore", tag: "MARKETING" },
      {
        t: "Keeping a plant running without a single hitch",
        tag: "INDUSTRIAL",
      },
    ],
  },
  {
    q: "Someone says 'trust me, this changes everything.' What's it about?",
    opts: [
      { t: "Medicine, or early diagnosis", tag: "HEALTH" },
      { t: "How everyday people access banking", tag: "FINTECH" },
      { t: "How machines think", tag: "AI" },
      { t: "Where all your files actually live", tag: "CLOUD" },
    ],
  },
  {
    q: "Pick the founder story you'd want to hear over chai:",
    opts: [
      {
        t: "Built something in a garage that now protects networks",
        tag: "SECURITY",
      },
      { t: "Left a job for a wild UAV idea", tag: "AUTO" },
      { t: "Started teaching AI to school kids in Kannada", tag: "EDU" },
      { t: "Turned a hyperlocal idea into a whole platform", tag: "LOCAL" },
    ],
  },
];

const EVENT_DATE = new Date(2026, 8, 19); // Sept 19, 2026 (month is 0-indexed)

/* ================= HELPERS ================= */
function track(eventName, params = {}) {
  // eslint-disable-next-line no-console
  console.log("[track]", eventName, params);
  if (typeof window !== "undefined" && typeof window.gtag === "function") {
    window.gtag("event", eventName, params);
  }
}

function matchPercent(score) {
  // each of the questions contributes at most 1 point to exactly one tag,
  // so a company's max possible score (tag1 + tag2) is questions.length.
  const raw = Math.max(0, Math.min(1, score / questions.length));
  return Math.min(99, Math.round(55 + raw * 44));
}

function buildReason(c, scores) {
  const s1 = scores[c.tag1] || 0;
  const s2 = scores[c.tag2] || 0;
  const parts = [];
  if (s1 > 0) parts.push({ label: TAG_LABELS[c.tag1], score: s1 });
  if (s2 > 0) parts.push({ label: TAG_LABELS[c.tag2], score: s2 });
  parts.sort((a, b) => b.score - a.score);
  if (parts.length === 0)
    return "You share their instinct for building something that matters.";
  if (parts.length === 1)
    return `Because your answers leaned toward ${parts[0].label}.`;
  return `Because your answers leaned toward ${parts[0].label} and ${parts[1].label}.`;
}

function shuffle(arr) {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

function companyCountLabel() {
  return `${companies.length} companies · Tech Meet 2026`;
}

function emptyScores() {
  const s = {};
  Object.keys(TAG_LABELS).forEach((k) => (s[k] = 0));
  return s;
}

function urgencyContent() {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const days = Math.round((EVENT_DATE - today) / 86400000);
  if (days > 1)
    return (
      <>
        🗓️ <b>{days} days</b> to Tech Meet 2026 · Sept 19, Belagavi
      </>
    );
  if (days === 1)
    return (
      <>
        🗓️ <b>Tomorrow!</b> Tech Meet 2026 · Sept 19, Belagavi
      </>
    );
  if (days === 0)
    return (
      <>
        🎉 <b>Today's the day!</b> Tech Meet 2026, Belagavi
      </>
    );
  return <>🎉 Thanks for playing — catch the recap from Tech Meet 2026!</>;
}

/* ================= COMPONENT ================= */
export default function BetcaTwinFinder() {
  const [messages, setMessages] = useState([]); // {id, role:'bot'|'user', text}
  const [typing, setTyping] = useState(false);
  const [interactive, setInteractive] = useState(null); // {type:'start'|'name'|'question', qIndex?}
  const [dots, setDots] = useState(() => questions.map(() => "idle")); // 'idle'|'current'|'done'
  const [headerSub, setHeaderSub] = useState(companyCountLabel());
  const [fakeInputText, setFakeInputText] = useState(
    "Tap an option above to reply",
  );
  const [userName, setUserName] = useState("");
  const [nameDraft, setNameDraft] = useState("");
  const [matchList, setMatchList] = useState([]); // [{c, score}]
  const [matchIdx, setMatchIdx] = useState(0);
  const [showSheet, setShowSheet] = useState(false);

  const idRef = useRef(0);
  const scoresRef = useRef(emptyScores());
  const chatLogRef = useRef(null);
  const nameInputRef = useRef(null);
  const startedRef = useRef(false);

  /* ----- message helpers ----- */
  const appendBot = useCallback((text) => {
    setMessages((m) => [...m, { id: ++idRef.current, role: "bot", text }]);
  }, []);
  const appendUser = useCallback((text) => {
    setMessages((m) => [...m, { id: ++idRef.current, role: "user", text }]);
  }, []);
  const addTyping = useCallback((cb, delay = 650) => {
    setTyping(true);
    setTimeout(() => {
      setTyping(false);
      cb();
    }, delay);
  }, []);

  /* ----- flow ----- */
  const askQuestion = useCallback(
    (i) => {
      setDots((d) => d.map((s, idx) => (idx === i ? "current" : s)));
      setHeaderSub(`Question ${i + 1} of ${questions.length}`);
      appendBot(questions[i].q);
      setTimeout(() => setInteractive({ type: "question", qIndex: i }), 350);
    },
    [appendBot],
  );

  const finishQuiz = useCallback(() => {
    setHeaderSub("Calculating your twin…");
    setFakeInputText("Result ready ↓");
    const greetName = userName ? `, ${userName}` : "";
    appendBot(
      `Alright${greetName}, that's everything I need 🎉 Let me find your twin among all ${companies.length} companies…`,
    );
    setTimeout(() => {
      addTyping(() => {
        const scores = scoresRef.current;
        const scored = shuffle(companies.slice())
          .map((c) => ({ c, score: scores[c.tag1] + scores[c.tag2] }))
          .sort((a, b) => b.score - a.score);
        const top = scored.slice(0, 8);
        setMatchList(top);
        setMatchIdx(0);
        track("quiz_completed", {
          top_match: top[0].c.name,
          top_match_pct: matchPercent(top[0].score),
        });
        setShowSheet(true);
      }, 900);
    }, 400);
  }, [userName, appendBot, addTyping]);

  const onAnswer = useCallback(
    (i, opt) => {
      appendUser(opt.t);
      scoresRef.current[opt.tag] += 1;
      track("question_answered", { question_index: i + 1, tag: opt.tag });
      setDots((d) => d.map((s, idx) => (idx === i ? "done" : s)));
      setInteractive(null);
      addTyping(() => {
        if (i + 1 < questions.length) askQuestion(i + 1);
        else finishQuiz();
      });
    },
    [appendUser, addTyping, askQuestion, finishQuiz],
  );

  const onNameSubmit = useCallback(
    (name) => {
      setUserName(name);
      appendUser(name ? name : "Skip");
      track("name_submitted", { has_name: !!name });
      setFakeInputText("Tap an option above to reply");
      setInteractive(null);
      addTyping(() => askQuestion(0));
    },
    [appendUser, addTyping, askQuestion],
  );

  const askName = useCallback(() => {
    setHeaderSub("Just one quick thing…");
    setFakeInputText("Type your name above");
    appendBot("First — what should I call you?");
    setTimeout(() => setInteractive({ type: "name" }), 350);
  }, [appendBot]);

  const onStart = useCallback(() => {
    appendUser("Find my twin →");
    track("quiz_start");
    setInteractive(null);
    addTyping(() => askName());
  }, [appendUser, addTyping, askName]);

  const startQuiz = useCallback(() => {
    appendBot(
      "Hi! I'm here to match you with your Belagavi Technology Companies Association (BeTCA) twin — the Belagavi tech company most like you. A few quick questions, then I'll introduce you 👋",
    );
    setTimeout(() => setInteractive({ type: "start" }), 400);
  }, [appendBot]);

  const retake = useCallback(() => {
    track("retake_click");
    setShowSheet(false);
    setMessages([]);
    scoresRef.current = emptyScores();
    setMatchList([]);
    setMatchIdx(0);
    setUserName("");
    setNameDraft("");
    setDots(questions.map(() => "idle"));
    setHeaderSub(companyCountLabel());
    setFakeInputText("Tap an option above to reply");
    setInteractive(null);
    setTimeout(startQuiz, 200);
  }, [startQuiz]);

  /* ----- mount ----- */
  useEffect(() => {
    if (startedRef.current) return; // guard against StrictMode double-invoke
    startedRef.current = true;
    startQuiz();
  }, [startQuiz]);

  /* auto-scroll on new content */
  useEffect(() => {
    const el = chatLogRef.current;
    if (el)
      requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight;
      });
  }, [messages, typing, interactive]);

  /* focus name input when it appears */
  useEffect(() => {
    if (interactive?.type === "name") {
      const t = setTimeout(() => nameInputRef.current?.focus(), 200);
      return () => clearTimeout(t);
    }
  }, [interactive]);

  /* track result_viewed whenever the sheet opens or the shown match changes */
  useEffect(() => {
    if (showSheet && matchList.length) {
      const entry = matchList[matchIdx];
      track("result_viewed", {
        company: entry.c.name,
        sector: entry.c.sector,
        match_pct: matchPercent(entry.score),
        rank: matchIdx + 1,
      });
    }
  }, [showSheet, matchIdx, matchList]);

  /* ----- derived (current result) ----- */
  const entry = matchList[matchIdx];
  const c = entry?.c;
  const pct = entry ? matchPercent(entry.score) : 0;
  const scores = scoresRef.current;

  const openShareIntent = (url) =>
    window.open(url, "_blank", "noopener,noreferrer,width=600,height=560");

  const shareTwitter = () => {
    if (!c) return;
    track("share_click", {
      platform: "twitter",
      company: c.name,
      match_pct: pct,
    });
    const shareLine = `I got matched with ${c.name} (${pct}% match) on the Belagavi Technology Companies Association (BeTCA) Twin Quiz! Play to find your match 👉`;
    const twitterText = `${shareLine} #TechMeet2026 #Belagavi`;
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(twitterText)}&url=${encodeURIComponent(QUIZ_URL)}`;
    openShareIntent(url);
  };

  const shareLinkedIn = () => {
    if (!c) return;
    track("share_click", {
      platform: "linkedin",
      company: c.name,
      match_pct: pct,
    });
    const shareLine = `I got matched with ${c.name} (${pct}% match) on the Belagavi Technology Companies Association (BeTCA) Twin Quiz! Play to find your match 👉`;
    // LinkedIn may ignore custom text and fall back to the page's Open Graph tags.
    const li = `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(QUIZ_URL)}&title=${encodeURIComponent("Find Your Belagavi Technology Companies Association (BeTCA) Twin")}&summary=${encodeURIComponent(shareLine)}&source=${encodeURIComponent("Belagavi Technology Companies Association (BeTCA)")}`;
    openShareIntent(li);
  };

  const onAnother = () => {
    setMatchIdx((i) => {
      const next = (i + 1) % matchList.length;
      track("meet_another_founder_click", { next_rank: next + 1 });
      return next;
    });
  };

  const cardHeadline = userName
    ? `${userName}'s Belagavi Technology Companies Association (BeTCA) twin is here 👋`
    : "Meet your Belagavi Technology Companies Association (BeTCA) twin — at Tech Meet 2026";

  return (
    <div className="betca-app">
      <Helmet>
        <title>
          Find Your Belagavi Technology Companies Association (BeTCA) Twin —
          Tech Meet 2026
        </title>

        {/* Open Graph / social share preview */}
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://mili8247.github.io/betca-twinmatch-quiz/"
        />
        <meta
          property="og:title"
          content="Find Your Belagavi Technology Companies Association (BeTCA) Twin — Tech Meet 2026"
        />
        <meta
          property="og:description"
          content="Answer 8 quick questions and find out which of 39 Belagavi tech companies is your Belagavi Technology Companies Association (BeTCA) twin. Made for Tech Meet 2026."
        />
        <meta
          property="og:image"
          content="https://mili8247.github.io/betca-twinmatch-quiz/og-image.png"
        />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />

        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Find Your Belagavi Technology Companies Association (BeTCA) Twin — Tech Meet 2026"
        />
        <meta
          name="twitter:description"
          content="Answer 8 quick questions and find out which of 39 Belagavi tech companies is your Belagavi Technology Companies Association (BeTCA) twin."
        />
        <meta
          name="twitter:image"
          content="https://mili8247.github.io/betca-twinmatch-quiz/og-image.png"
        />

        {/* Fonts */}
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link
          href="https://fonts.googleapis.com/css2?family=Baloo+2:wght@500;600;700;800&family=Inter:wght@400;500;600;700&display=swap"
          rel="stylesheet"
        />

        {/* Google tag (gtag.js) */}
        <script
          async
          src="https://www.googletagmanager.com/gtag/js?id=G-YV699QGW9W"
        ></script>
        <script>{`
          window.dataLayer = window.dataLayer || [];
          function gtag(){dataLayer.push(arguments);}
          gtag('js', new Date());
          gtag('config', 'G-YV699QGW9W');
        `}</script>
      </Helmet>

      <div className="app">
        <div className="header">
          <div className="header-avatar">BT</div>
          <div className="header-text">
            <div className="header-title">BeTCA Twin Finder</div>
            <div className="header-sub">{headerSub}</div>
          </div>
        </div>

        <div className="urgency-banner">{urgencyContent()}</div>

        <div className="progress-row">
          {dots.map((s, i) => (
            <div
              key={i}
              className={`dot${s === "done" ? " done" : ""}${s === "current" ? " current" : ""}`}
            >
              <span />
            </div>
          ))}
        </div>

        <div className="chat-log" ref={chatLogRef}>
          {messages.map((m) =>
            m.role === "bot" ? (
              <div className="row bot" key={m.id}>
                <div className="bot-wrap">
                  <div className="bot-mini">🍃</div>
                  <div className="bubble bot">{m.text}</div>
                </div>
              </div>
            ) : (
              <div className="row user" key={m.id}>
                <div className="bubble user">{m.text}</div>
              </div>
            ),
          )}

          {typing && (
            <div className="row bot typing-row">
              <div className="bot-mini">🍃</div>
              <div className="typing-bubble">
                <i />
                <i />
                <i />
              </div>
            </div>
          )}

          {interactive?.type === "start" && (
            <div className="chips">
              <button className="chip start" type="button" onClick={onStart}>
                Find my twin →
              </button>
            </div>
          )}

          {interactive?.type === "name" && (
            <>
              <div className="name-input-row">
                <input
                  ref={nameInputRef}
                  type="text"
                  className="name-input"
                  placeholder="Type your first name…"
                  maxLength={24}
                  autoComplete="off"
                  value={nameDraft}
                  onChange={(e) => setNameDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      const v = nameDraft.trim();
                      if (v) onNameSubmit(v);
                    }
                  }}
                />
                <button
                  className="name-send"
                  type="button"
                  onClick={() => {
                    const v = nameDraft.trim();
                    if (v) onNameSubmit(v);
                  }}
                >
                  ➤
                </button>
              </div>
              <button
                className="name-skip"
                type="button"
                onClick={() => onNameSubmit("")}
              >
                Skip — just call me Founder
              </button>
            </>
          )}

          {interactive?.type === "question" && (
            <div className="chips">
              {questions[interactive.qIndex].opts.map((o, idx) => (
                <button
                  key={idx}
                  className="chip"
                  type="button"
                  onClick={() => onAnswer(interactive.qIndex, o)}
                >
                  {o.t}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="fake-inputbar">
          <div className="fake-input">{fakeInputText}</div>
          <div className="fake-send">➤</div>
        </div>

        {/* ---- Reveal sheet ---- */}
        <div className={`sheet-backdrop${showSheet ? " show" : ""}`}>
          <div className="sheet">
            <div className="sheet-handle" />
            <div className="card-top">
              <div className="badge-row">
                <div className="badge-circle">🍃</div>
                <div className="badge-text">
                  <div className="badge-title">BeTCA</div>
                  <div className="badge-sub">
                    Belagavi Technology Companies Association
                  </div>
                </div>
              </div>
              <div className="card-headline">{cardHeadline}</div>
            </div>
            <div className="card-body">
              <div className="avatar-wrap">
                <div className="card-avatar">
                  {c?.photo ? (
                    <img
                      src={c.photo}
                      alt={`${c.founder} — ${c.name}`}
                      className="show"
                    />
                  ) : (
                    <span>{c?.emoji}</span>
                  )}
                </div>
              </div>
              <div className="card-name">{c?.name}</div>
              <div className="card-founder">{c?.founder}</div>
              <div className="match-pct">{pct}% Match</div>
              <div className="sector-pill">{c?.sector}</div>
              <div className="card-divider" />
              <div className="card-blurb">{c?.blurb}</div>
              <div className="match-reason">
                {c ? buildReason(c, scores) : ""}
              </div>
              <div className="card-meta">
                <span>
                  <b>{matchIdx + 1}</b> of <span>{matchList.length}</span> good
                  matches for you
                </span>
              </div>

              <div className="sheet-actions">
                <button
                  className="btn btn-outline"
                  type="button"
                  onClick={onAnother}
                >
                  Meet another founder →
                </button>
                <div className="share-row">
                  <button
                    className="btn-social btn-twitter"
                    type="button"
                    onClick={shareTwitter}
                  >
                    Share on X
                  </button>
                  <button
                    className="btn-social btn-linkedin"
                    type="button"
                    onClick={shareLinkedIn}
                  >
                    Share on LinkedIn
                  </button>
                </div>
                <a
                  className="btn btn-secondary"
                  href="https://tech-meet.betca.org/"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    track("register_click", {
                      company: c?.name,
                      match_pct: pct,
                    })
                  }
                >
                  Register for Tech Meet 2026
                </a>
              </div>
              <button className="retake" type="button" onClick={retake}>
                🔄 Take the quiz again
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
